// The page address the analytics and advertising tags are told about.
//
// A page address can carry personal values in its query string: the email
// verification link holds the person's email address, user name and
// confirmation code, and other routes take values of their own. The tags only
// need the path and the campaign parameters, so everything else is left out:
// the query string is cut down to campaign parameters and ad-click ids, and the
// fragment is dropped.

// Campaign parameters all start "utm_". Anything else is reported only if it is
// one of these ad-click ids.
const CAMPAIGN_PARAM_PREFIX = 'utm_';
const AD_CLICK_PARAMS: readonly string[] = [
  'gclid',
  'gclsrc',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
  'dclid',
  'srsltid',
  'fbclid',
  'msclkid',
  'ttclid',
  'rdt_cid',
];

const isReportableName = (name: string): boolean =>
  name.startsWith(CAMPAIGN_PARAM_PREFIX) || AD_CLICK_PARAMS.includes(name);

export type ReportablePageAddress = {
  page_location: string;
  page_path: string;
  page_referrer: string;
};

// A campaign value has no reason to be an email address; one that is is dropped.
const isReportableParam = ([name, value]: [string, string]): boolean =>
  isReportableName(name.toLowerCase()) && !value.includes('@');

/** The query string with only campaign parameters left: '?utm_source=ad', or ''. */
export const reportableSearch = (search: string): string => {
  const kept = Array.from(new URLSearchParams(search).entries()).filter(isReportableParam);
  const query = new URLSearchParams(kept).toString();
  return query ? `?${query}` : '';
};

type AddressParts = { origin: string; path: string };

const NO_ADDRESS: AddressParts = { origin: '', path: '' };

// Scheme and host are read separately: `origin` is "null" for the app's own
// scheme in some engines.
const reportableParts = (address: string): AddressParts => {
  try {
    const url = new URL(address);
    return {
      origin: `${url.protocol}//${url.host}`,
      path: `${url.pathname}${reportableSearch(url.search)}`,
    };
  } catch {
    return NO_ADDRESS;
  }
};

/**
 * What to tell the tags about the current page and where the visitor came
 * from, given the full page address and the document's referrer.
 */
export const reportablePageAddress = (
  pageAddress: string,
  referrer: string,
): ReportablePageAddress => {
  const page = reportableParts(pageAddress);
  const from = reportableParts(referrer);
  return {
    page_location: `${page.origin}${page.path}`,
    page_path: page.path,
    page_referrer: `${from.origin}${from.path}`,
  };
};
